import Table from 'components/Table';
import PageContainer from 'components/PageContainer';
import { useFilters, usePagination } from 'further-ui/hooks';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import SearchField from 'components/SearchField';
import trpcApi, { ApiResponses } from 'lib/trpcClient';
import LinkedAccountsColumn from './LinkedAccountsColumn';
import LastLoginColumn from './LastLoginColumn';
import ActionsColumn from './ActionsColumn';
import { dateToLabel } from 'further-ui/utils';
import { FirmSelector } from 'components/FirmSelector';

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Users', isActive: true },
];

type FurtherUser = ApiResponses['furtherUser']['list']['results'][number];

const FurtherUsers: React.FC = () => {
  const {
    page,
    rowsPerPage,
    order,
    orderBy,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    toFirstPage,
  } = usePagination({
    id: 'further-users-table',
    orderBy: 'email',
    order: 'asc',
  });

  const { filters, handleChangeFilters } = useFilters('further-users-table', {
    onFiltersChange: toFirstPage,
    defaultFilters: { search: '', firmId: '' },
  });

  const { data: users, isFetching } = trpcApi.furtherUser.list.useQuery({
    order,
    orderBy,
    rowsPerPage,
    page,
    search: filters.search,
    firmId: filters.firmId,
  });

  const columns = [
    { label: 'Email', key: 'email' },
    {
      label: 'Linked accounts',
      key: 'linkedAccounts',
      sort: false,
      render: (elm: FurtherUser) => (
        <LinkedAccountsColumn
          linkedInvestors={elm.linkedInvestors}
          linkedAdviser={elm.linkedAdviser}
          linkedOrgUser={elm.linkedOrgUser}
        />
      ),
    },
    {
      label: 'Date created',
      key: 'createdAt',
      render: (elm: FurtherUser) => dateToLabel(elm.createdAt),
    },
    {
      label: 'Last login',
      key: 'lastLoginDate',
      sort: false,
      render: (elm: FurtherUser) => (
        <LastLoginColumn lastLogin={elm.lastLogin} />
      ),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm: FurtherUser) => <ActionsColumn furtherUser={elm} />,
    },
  ];

  return (
    <PageContainer heading="Users" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters pageId="further-users-table">
          <Filter>
            <SearchField
              autoSearchOnDebounce
              name="search"
              placeholder="Search by email"
              fullWidth
              value={filters.search}
              allowEmptyQuery
              onSearch={(search) => {
                handleChangeFilters({ search });
              }}
            />
          </Filter>
          <Filter>
            <FirmSelector
              firmId={filters.firmId}
              onChange={(firmId) => {
                handleChangeFilters({ firmId });
              }}
            />
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={handleRequestSort}
          order={order}
          orderBy={orderBy}
          tablebody={users?.results ?? []}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          count={users?.total}
          pagination={true}
          loading={isFetching}
          variant="nohover"
        />
      </PageContent>
    </PageContainer>
  );
};

export default FurtherUsers;
